/** 3 -> 4: the stack store grows two columns and each hero a racial charge.
 *
 * Three things arrived at once and all three needed somewhere in state to live:
 *
 *  - `flags2`, a second flag word. The first ran out at bit 31 and three bits
 *    had already been minted on top of taken ones (see `battle/flags.ts`), so
 *    the fix was a new column rather than a fourth gap-hunt.
 *  - `cooldown`, rounds until a stack's ACTIVATED ability is ready again.
 *  - `racialUsed` on each of a battle's two heroes: the one charge a faction
 *    racial gets per fight.
 *
 * A fourth thing arrived with them and is not on the battle: `caravans`, a new
 * root entity map, and the `caravan` id counter beside it. An old save has
 * neither, so the rung writes an empty map and a zero counter - which is
 * exactly the state a game that never sent one is in, so nothing is lost.
 *
 * All three battle fields are zero/false at the start of a battle, so a save paused mid-fight
 * resumes with every stack's second word clear, every cooldown ready and both
 * racials unspent. That is a small gift to a loaded game and the only honest
 * answer available: the old file recorded no cooldowns because nothing could
 * set one, and there is no earlier value to recover.
 *
 * The columns are written the length the store says it is - `stacks.n` - and
 * not the length of a sibling column, because a battle that summoned grew its
 * columns past `n` at one point in its life and the store's own count is the
 * authority on how many slots exist.
 */

import type { Migration } from "./types.js";
import { isRecord, readSave, writeSave, type Tree } from "./tree.js";

const TA_TAG = "__ta";
const TA_VALUES = "v";

/** A zero-filled i32 column in the encoded typed-array shape. */
const zeros = (n: number): Tree => ({ [TA_TAG]: "i32", [TA_VALUES]: new Array(n).fill(0) });

function columnLength(stacks: Record<string, unknown>): number {
  const n = stacks["n"];
  if (typeof n === "number" && Number.isInteger(n) && n >= 0) return n;
  // No `n` to trust: fall back to a column that must exist on every store.
  const flags = stacks["flags"];
  if (isRecord(flags) && Array.isArray(flags[TA_VALUES])) return flags[TA_VALUES].length;
  return 0;
}

export const migration0004: Migration = {
  from: 3,
  to: 4,
  describe:
    "add the second stack flag word, ability cooldowns, the racial charge and caravans",
  migrate(raw: unknown): Record<string, unknown> | null {
    const save = readSave(raw);
    if (save === null) return null;

    // The root half: an empty caravan map and the counter that mints its ids.
    // Written before the battle half so a save with no battle still gets it.
    const counters = save.state["counters"];
    const withRoot: Tree = {
      ...save.state,
      ...(Object.prototype.hasOwnProperty.call(save.state, "caravans")
        ? {} : { caravans: {} }),
      ...(isRecord(counters) && !Object.prototype.hasOwnProperty.call(counters, "caravan")
        ? { counters: { ...counters, caravan: 0 } } : {}),
    };

    const battle = withRoot["battle"];
    if (!isRecord(battle)) return writeSave(save, withRoot, 4);

    const next: Record<string, unknown> = { ...battle };

    const stacks = battle["stacks"];
    if (isRecord(stacks)) {
      const n = columnLength(stacks);
      next["stacks"] = {
        ...stacks,
        ...(Object.prototype.hasOwnProperty.call(stacks, "flags2") ? {} : { flags2: zeros(n) }),
        ...(Object.prototype.hasOwnProperty.call(stacks, "cooldown") ? {} : { cooldown: zeros(n) }),
      };
    }

    const heroes = battle["heroes"];
    if (Array.isArray(heroes)) {
      next["heroes"] = heroes.map((h) =>
        isRecord(h) && !Object.prototype.hasOwnProperty.call(h, "racialUsed")
          ? { ...h, racialUsed: false }
          : h);
    }

    return writeSave(save, { ...withRoot, battle: next }, 4);
  },
};
